// Detection Engine System 1 — public entry (spec §5).
//
// Один движок, одна детекция, два рендера. Контракт I/O:
//   INPUT step6.L1..L7[1..N] (touched)
//     → детекция конфигураций (§3.1)
//     → выбор ОСНОВНОГО ПАТТЕРНА (§3.3)
//         ├─► красная линия = кривая паттерна (§2.1) → geometry_class +
//         │   level_band + forward (§2.3/2.5/2.8)
//         └─► §V cards[1..3] + evidence + stub fallbacks (§3.4–3.6)
//
// Детерминированный, БЕЗ LLM. step3.rating (синусоида шага 4) — ВНЕ System 1.

import { normalizeStages, silhouette, filled } from './input.js';
import { detectAll } from './detectors.js';
import { resolve } from './selection.js';
import { carrierCurve } from './carrier.js';
import { classifyGeometry, forwardProjection } from './geometry.js';
import { selectCards } from './cards.js';
import { carrierConfigOf, carrierSourceLines, currentStanding } from './render_keys.js';

/**
 * Прогон движка над входом визарда.
 * @param input  step6-этапы (см. normalizeStages — принимает несколько форм)
 * @returns { mainPattern, redLine, cards, meta }
 */
export function detect(input) {
  const { stages, active } = normalizeStages(input);
  const detections = detectAll(stages, active);
  const { main, tier, ranked } = resolve(detections, stages, active);

  // Слой 1 — красная линия.
  const carrier = carrierCurve(main, stages, active);
  const geom = classifyGeometry(carrier.c, carrier.fractional);

  // Forward-проекция §VII (s384): анкер к последней точке СИЛУЭТА (среднее active-
  // слоёв per stage), НЕ к несущей — иначе «две дороги» уходят в сторону несущей
  // (01 обе вниз, 07 обе вверх). Silhouette = та же кривая, что §I показывает.
  const silhouetteCurve = silhouette(stages, active); // touched-per-stage (s443.d5 Ф1)
  // Пустой этап силуэта = null (s836.d3); forwardProjection берёт slope = c[N-1]−c[N-2] и
  // якорь по последней точке — null дал бы NaN. Читаем через filled() (форма, не утверждение);
  // на all-touched корпусе null нет → filled() тождественно, снапшоты не двигаются.
  const forward = forwardProjection(filled(silhouetteCurve));

  // Слой 2 — §V карточки.
  const cardSel = selectCards(ranked, main, stages, active);

  // §V section_absent — ратифицированный триггер s417.d1 (перенесён из деривера Track B).
  // Конъюнкция: ноль combination-карточек И нулевой спред у ВСЕХ семи слоёв одновременно.
  // Плоский ввод даёт лишь ложную карточку «moving as one» — снимаем её; раздел удаляется и
  // документ перенумеровывается. НЕ scatter (там сигнал есть, карточка F.7 остаётся) и НЕ один
  // слабый слой (спред ненулевой → раздел печатается). См. §V-спека / D&R s417.d1.
  // "zero spread across all seven layers" — over the TOUCHED (active) set, matching the engine's
  // touched-aware model (untouched sliders default to 0 and are excluded from interpretation, §1).
  // Spread over all-7 would let an untouched 0 inflate max−min and MISS a flat partial-touch
  // input (the spurious card would still print). Active-only: the whole active×stage matrix at a
  // single value = the only shape with NO signal (no movement in time AND no between-layer split),
  // so the only emittable card is the spurious flat "moving as one" — suppressed. A current-
  // singleton flat at a different level (Lasting +2, rest +1) has a split → NOT absent; one weak
  // layer shifted → NOT absent; scatter → NOT absent (all carry real signal).
  const activeVals = stages.flatMap((s) => active.map((L) => s.values[L]).filter(Number.isFinite));
  const spreadZero = active.length > 0 && activeVals.length > 0 &&
    Math.max(...activeVals) - Math.min(...activeVals) === 0;
  const sectionAbsent = spreadZero;
  const cards = sectionAbsent ? [] : cardSel.cards;

  return {
    mainPattern: {
      family: main.family,
      type: main.type,
      layers: main.layers.slice(),
      tier,
      strength: main.strength ?? 0,
      routing: main.routing ?? null,
    },
    redLine: {
      carrier: carrier.c,
      fractional: carrier.fractional,
      members: carrier.members,
      carrierConfig: carrierConfigOf(main, carrier), // §2.9 row (6 values) — s322
      carrierSourceLines: carrierSourceLines(main, carrier), // pale draw set per s310.d2 — s322
      paleLines: carrier.paleLines, // complement of active (context; NOT the draw set)
      eyebrow: carrier.eyebrow,
      geometryClass: geom.geometryClass,
      levelBand: geom.levelBand,
      forward, // §2.8, anchored to the silhouette (s384) — not geom.forward (carrier)
    },
    currentStanding: currentStanding(stages, active), // §III/§IV "where it stands now" — s322
    cards,
    meta: {
      stageCount: stages.length,
      activeLayers: active.slice(),
      detectionCount: detections.length,
      realCards: sectionAbsent ? 0 : cardSel.real.length,
      stubCards: sectionAbsent ? 0 : cardSel.stubs.length,
      // present only when the section is removed (zero_spread_all_layers, s417.d1) → assembler
      // renumbers. Conditional so non-absent snapshots stay byte-equal to the frozen datasets.
      ...(sectionAbsent ? { sectionAbsent: true } : {}),
    },
  };
}

export { normalizeStages } from './input.js';
export { detectAll } from './detectors.js';
export { resolve, selectMainPattern, applyMutex } from './selection.js';
export { carrierCurve } from './carrier.js';
export { classifyGeometry } from './geometry.js';
export { selectCards, stubFires } from './cards.js';
export { carrierConfigOf, carrierSourceLines, currentStanding } from './render_keys.js';
export { deriveCarrierStages } from './carrierStages.js';
